/** Shown wherever the API gave us nothing, so a missing field never breaks a layout (US-008 AC-003). */
export const EMPTY_VALUE = '—';

const LONG_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const SHORT_DATE = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const WINDOW_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const WINDOW_TIME = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

/**
 * "July 29, 2026, 6:00 PM EST": the UTC instant rendered in the user's own time zone (Proweb-38). No
 * `timeZone` option, deliberately — these two formatters must follow the machine's zone. Date and time
 * are formatted separately and joined, because one combined Intl format inserts "at" between them,
 * which is not the approved format.
 */
export function formatWindowTime(iso: string): string {
  const instant = new Date(iso);
  return `${WINDOW_DATE.format(instant)}, ${WINDOW_TIME.format(instant)}`;
}

function parse(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value.length === 0) {
    return null;
  }

  const parsed = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** "Jan 1, 2025" — the format US-008 AC-001 fixes for Issue Date. */
export function formatLongDate(value: string | null | undefined): string {
  const parsed = parse(value);
  return parsed === null ? EMPTY_VALUE : LONG_DATE.format(parsed);
}

/** "MM/DD/YYYY" — the format US-008 AC-001 fixes for Date of Birth. */
export function formatShortDate(value: string | null | undefined): string {
  const parsed = parse(value);
  return parsed === null ? EMPTY_VALUE : SHORT_DATE.format(parsed);
}

/** A service period collapses to a single date when both ends match. */
export function formatDateRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const start = formatShortDate(from);
  const end = formatShortDate(to);

  if (start === EMPTY_VALUE) {
    return end;
  }

  return end === EMPTY_VALUE || start === end ? start : `${start} - ${end}`;
}

export function formatCurrency(value: number | null | undefined): string {
  return value === null || value === undefined ? EMPTY_VALUE : CURRENCY.format(value);
}

export function formatText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  const text = String(value).trim();
  return text.length === 0 ? EMPTY_VALUE : text;
}

/**
 * Returns the URL only when it is safe to put in an `href`.
 *
 * The value comes from the BF API, and React does not block a `javascript:` href — a hostile or
 * mistaken value would run script in the portal origin with policyholder PHI on screen. Only https
 * is accepted.
 */
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.length === 0) {
    return null;
  }

  try {
    // https only. The link sits beside policyholder name, date of birth and claims, so a plaintext
    // fetch would tell an on-path observer which policy's plan document was opened.
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** What the Check Number shows when there is none: the same dash every other empty field shows. */
export const NO_CHECK_NUMBER = EMPTY_VALUE;

/** A check number of null, blank or all zeros means no check was issued, so it shows the empty-value dash. */
export function formatCheckNumber(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return NO_CHECK_NUMBER;
  }

  const text = String(value).trim();
  return text.length === 0 || /^0+$/.test(text) ? NO_CHECK_NUMBER : text;
}
